/**
 * /product-roadmap-example — SEO landing page targeting the "product
 * roadmap example" / "product roadmap examples" / "roadmap template"
 * cluster.
 *
 * Differentiation angle: a searcher for a "product roadmap example"
 * wants formats — Now-Next-Later, timeline/Gantt, theme-based,
 * outcome-based, release-based. This page hands them the actual formats
 * (short example + when to use each) so they get something usable, then
 * shows the catch: every roadmap in a slide or a spreadsheet is severed
 * from the decisions underneath it. Re-prioritize once and nobody
 * resyncs. Draftlize links each roadmap item to the decisions it depends
 * on, so when a decision changes, the dependent items auto-flag stale.
 *
 * English-only + inline (no i18n), mirroring DecisionLog.tsx /
 * DecisionLogTemplate.tsx and the comparison pages. Chinese variant
 * lives in ProductRoadmapExampleZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function ProductRoadmapExample() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/product-roadmap-example"
      documentTitle="Product Roadmap Examples (That Don't Go Stale) · Draftlize"
      documentDescription="Five product roadmap examples you can copy — Now-Next-Later, timeline / Gantt, theme-based, outcome-based, release-based — with a short note on when each one fits. The catch: every product roadmap example is a snapshot severed from the decisions underneath it, so a single re-prioritization leaves it wrong. In Draftlize each roadmap item links to the decisions it depends on, and items auto-flag stale when an upstream decision changes."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Examples · Product Roadmaps</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            Product roadmap examples —<br/>
            <em>and the kind that updates itself.</em>
          </h1>
          <p className="lede">
            You came for a <em>product roadmap example</em> — a format to copy, a shape that makes sense of what's shipping when. There are five good ones below, each with a short example and the moment it fits; take whichever matches how your team thinks. But every one shares a flaw the format can't fix: a roadmap is a picture of priorities, and priorities are <em>decisions</em> — about pricing, scope, sequencing, what you're not building. Put the picture in a slide or a spreadsheet and it's severed from those decisions the instant you save it. Reprioritize once and the roadmap is fiction nobody resynced. Draftlize keeps the same formats and links every item to the decisions underneath it, so when a decision moves, the items that depended on it flag themselves stale.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The examples</span>
            <h2 className="serif">Five formats. <em>Pick the one that fits.</em></h2>
          </div>
          <p>These are the product roadmap examples teams actually use. Each works in a doc, a slide, or a spreadsheet — copy the one that matches how you plan. The next section is about what changes when each item stops being a box on a slide and starts carrying the decisions it rests on.</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: 'Now · Next · Later',
              hint: 'Default',
              body: 'Three buckets instead of dates. "Now" is in flight, "Next" is committed, "Later" is directional. Example: Now — SSO + audit log; Next — usage-based billing; Later — mobile. When to use: early-stage or anything genuinely uncertain, where promising a March date is a lie you\'ll pay for. The honest default.',
            },
            {
              k: 'Timeline / Gantt',
              hint: 'Dates',
              body: 'Initiatives laid against a calendar, with bars and dependencies. Example: Q1 — billing rework (Jan–Feb), then SSO (Feb–Mar); Q2 — mobile beta. When to use: hard external deadlines, a launch tied to a contract or an event, or stakeholders who need to see sequencing. Powerful and dangerous — the dates read as promises.',
            },
            {
              k: 'Theme-based',
              hint: 'Strategy',
              body: 'Organized by strategic theme, not feature. Example: "Reduce time-to-value," "Win enterprise," "Cut infra cost" — each holding the work that serves it. When to use: when you need leadership and the team aligned on why before what, and you want room to swap features under a theme without renegotiating the whole plan.',
            },
            {
              k: 'Outcome-based',
              hint: 'Metrics',
              body: 'Committed to results, not output. Example: "Lift activation from 28% to 40%," "Cut p95 latency below 200ms" — features listed as bets toward each number. When to use: a mature product with metrics it trusts, and a team allowed to change tactics as long as the outcome holds. The format that resists feature-factory drift.',
            },
            {
              k: 'Release-based',
              hint: 'Versions',
              body: 'Structured around shipping milestones. Example: v2.1 — SAML + SCIM; v2.2 — usage-based billing; v3.0 — workspace redesign. When to use: versioned products, anything with a changelog or release notes, or platforms where customers plan around your versions. Maps cleanly to what users actually receive.',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          That's the roadmap — in whichever format fits. Below: why the picture rots the moment a priority moves — and what we do about it.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a roadmap <em>goes stale.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">The format isn't the problem — the disconnect is</h3>
            <p>Now-Next-Later or a Gantt chart, the shape is fine. What kills a roadmap is that it's a rendering of decisions it isn't attached to. The "why" behind every item — the pricing call, the scope cut, the sequencing trade — lives somewhere else, or nowhere, and the slide only shows the conclusion.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Reprioritize once and nobody resyncs</h3>
            <p>You move "billing" from Next to Now because a deal demanded it. That single change ripples: the launch sequence, the dependent specs, three downstream items that assumed the old order. In a spreadsheet none of them know. The roadmap presented on Monday is already wrong by Wednesday, and the wrongness is invisible.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">It's a deliverable, not a source of truth</h3>
            <p>A roadmap in a slide deck exists to be presented, then it's stale until the next time someone rebuilds it by hand for the next review. Between presentations it drifts from what's actually being built, and everyone quietly learns to trust the standup over the roadmap.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same roadmap, <em>made live.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Each item carries the decisions it rests on</h3>
            <p>In Draftlize a roadmap item isn't a box on a slide — it's a card linked to the decisions underneath it: the pricing call it assumes, the scope it was cut to, the sequencing it depends on. Open the item and you see the "why," not just the "what." The format stays Now-Next-Later or release-based; the wiring is new.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Change a decision, dependent items flag stale</h3>
            <p>Because each item links to real decisions and not just to a date, changing one decision turns every roadmap item that rested on it stale automatically — the way a build system invalidates everything downstream of a changed file. Move pricing and the items that assumed the old price raise their hand, instead of waiting to embarrass you in a demo.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">An AI agent reads and writes the roadmap</h3>
            <p>Claude Code or Cursor, over MCP, reads the decisions behind every roadmap item before it drafts the next spec, and writes new items and decisions back into the same shape. The roadmap stops being a slide rebuilt for each review and becomes context that gets read on every turn — and stays true between them.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A roadmap is a picture of your priorities. Priorities are decisions — and a picture can't tell you when one of them changed.
        </blockquote>
        <cite>Keep the format you like. Let the substrate keep it current.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What does a good product roadmap look like?', a: 'A good roadmap shows outcomes and themes over time rather than a dated feature list — commonly in a Now-Next-Later, timeline, or release-based format. The best ones make the "why" visible: each item ties back to the strategy and the decisions it rests on, so a reader sees priorities, not just boxes.' },
            { q: 'What are the main types of product roadmap?', a: 'The common formats are Now-Next-Later (confidence decreases as you look further out), timeline or Gantt-style (calendar-based, best when dates are firm), theme-based (organized by outcome or problem), and release-based (grouped by ship). Pick by how much certainty you actually have — a precise timeline you cannot honor is worse than an honest Now-Next-Later.' },
            { q: 'What should a product roadmap include?', a: 'Themes or outcomes, the items under them, rough timeframes or horizons, owners, and ideally the goal each item serves. What it should not be is a backlog with dates — a roadmap communicates direction and priority, and overloading it with committed dates turns it into a promise you break.' },
            { q: 'How do you keep a roadmap from going stale between reviews?', a: 'A roadmap rebuilt by hand for each review drifts the moment the meeting ends. Draftlize links each item to the decisions underneath it, so changing one decision turns every item that rested on it stale automatically — the roadmap stays true between reviews instead of being trusted less than the standup.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Build a roadmap that stays true <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Copy any format above, or spin up a project and let the agent build the roadmap from your existing docs — then keep every item, and the decisions it depends on, in sync for you.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
